import { useState } from 'react';
import type {
  AttributeLibraryResponse,
  CapabilityComponentSubjectType,
  CapabilityDefinitionDetailResponse,
  CapabilityLibraryResponse,
  ExperienceAreaLibraryResponse,
  LibraryDefinitionAdminResponse,
  SkillLibraryResponse,
} from '../../api/types';
import { apiPatch, apiPost, describeError } from '../../api/client';
import { useApiResource } from '../../hooks/useApiResource';
import { ErrorState, LoadingState } from '../StateMessage';
import { ActionFeedback, AdminForm, ConfirmAction, Field, StatusTag, useAction } from './adminShared';
import styles from './admin.module.css';

/**
 * The Capability library (Mission 166, implementing the specification
 * `docs/DOMAIN_DECISIONS.md`'s "Composite Concepts" (Mission 165)
 * authorised, Part 2).
 *
 * ---------------------------------------------------------------------
 * WHAT A CAPABILITY IS, AND WHAT THIS SCREEN MUST NOT LET IT BECOME
 *
 * A Capability is a named, weighted composite of Attribute/Skill/
 * Experience-Area components — never directly assessed, always computed
 * fresh from whichever components currently have a known value. This
 * screen only ever defines the composite (name, description, sensitivity,
 * components); it never shows a computed value here, because a value is
 * always relative to one employee, not the definition. There is no
 * Achievement component type on this picker — Achievement categorisation
 * is an unresolved product decision (Mission 165), not something this
 * screen invents.
 *
 * COMPONENTS ARE FIXED AT CREATION
 *
 * Unlike a Role's requirements, there is no "add a component" or "remove a
 * component" action here once a Capability exists — only name/description/
 * sensitivity may be edited in place. Changing what a Capability is made of
 * changes what its already-recorded values meant, so a meaning change is
 * deprecate the old definition and create a new one, the same discipline
 * `AttributeLibrarySection` already applies to scale wording.
 * ---------------------------------------------------------------------
 */
export function CapabilityLibrarySection() {
  const capabilities = useApiResource<CapabilityLibraryResponse>('/admin/capabilities');
  const attributes = useApiResource<AttributeLibraryResponse>('/admin/attribute-definitions');
  const skills = useApiResource<SkillLibraryResponse>('/admin/skills');
  const experienceAreas = useApiResource<ExperienceAreaLibraryResponse>('/admin/experience-areas');
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <section className={styles.section} aria-label="Capability library">
      <h2 className={styles.sectionTitle}>Capability library</h2>
      <p className={styles.sectionNote}>
        A Capability is a weighted composite of Attributes, Skills and Experience Areas — never a rating anyone
        enters directly, and never a single "employee score". Its value is always computed fresh for one employee
        from whichever components are currently known about them.
      </p>

      {capabilities.status === 'loading' ? <LoadingState label="Loading the capability library…" /> : null}
      {capabilities.status === 'failed' ? (
        <ErrorState title="The capability library could not be loaded" detail={describeError(capabilities.error)} />
      ) : null}

      {capabilities.status === 'loaded' ? (
        <>
          <ul className={styles.list}>
            {capabilities.data.capabilities.map((entry) => (
              <li key={entry.id} className={styles.row}>
                <span className={styles.rowName}>{entry.name}</span>
                <StatusTag status={entry.status} />
                <span className={styles.rowMeta}>{entry.sensitivity === 'elevated' ? 'Elevated' : 'Standard'}</span>
                <span className={styles.rowActions}>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={() => setEditing(editing === entry.id ? null : entry.id)}
                  >
                    {editing === entry.id ? 'Close' : 'Manage'}
                  </button>
                </span>
                {editing === entry.id ? (
                  <CapabilityEditorForm
                    capability={entry}
                    onDone={() => {
                      setEditing(null);
                      capabilities.reload();
                    }}
                  />
                ) : null}
              </li>
            ))}
          </ul>

          {creating ? (
            attributes.status === 'loaded' && skills.status === 'loaded' && experienceAreas.status === 'loaded' ? (
              <CapabilityCreator
                attributes={attributes.data.attributes.filter((a) => a.status === 'active')}
                skills={skills.data.skills.filter((s) => s.status === 'active')}
                experienceAreas={experienceAreas.data.experienceAreas.filter((e) => e.status === 'active')}
                onDone={() => {
                  setCreating(false);
                  capabilities.reload();
                }}
              />
            ) : (
              <LoadingState label="Loading the component libraries…" />
            )
          ) : (
            <div className={styles.actions}>
              <button type="button" className={styles.primaryButton} onClick={() => setCreating(true)}>
                Define a new capability
              </button>
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}

const COMPONENT_TYPE_LABEL: Record<CapabilityComponentSubjectType, string> = {
  attribute: 'Attribute',
  skill: 'Skill',
  experience_area: 'Experience area',
};

interface DraftComponent {
  subjectType: CapabilityComponentSubjectType;
  subjectId: string;
  weight: string;
  experienceCeilingYears: string;
}

function CapabilityCreator({
  attributes,
  skills,
  experienceAreas,
  onDone,
}: {
  attributes: AttributeLibraryResponse['attributes'];
  skills: LibraryDefinitionAdminResponse[];
  experienceAreas: LibraryDefinitionAdminResponse[];
  onDone: () => void;
}) {
  const subjectsByType: Record<CapabilityComponentSubjectType, { id: string; name: string }[]> = {
    attribute: attributes,
    skill: skills,
    experience_area: experienceAreas,
  };

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sensitivity, setSensitivity] = useState<'standard' | 'elevated'>('standard');
  const [components, setComponents] = useState<DraftComponent[]>([
    { subjectType: 'attribute', subjectId: attributes[0]?.id ?? '', weight: '1', experienceCeilingYears: '5' },
  ]);
  const { state, run } = useAction();

  function addComponent() {
    setComponents((prev) => [
      ...prev,
      { subjectType: 'attribute', subjectId: attributes[0]?.id ?? '', weight: '1', experienceCeilingYears: '5' },
    ]);
  }

  function removeComponent(index: number) {
    setComponents((prev) => prev.filter((_, i) => i !== index));
  }

  function updateComponent(index: number, patch: Partial<DraftComponent>) {
    setComponents((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  }

  async function submit() {
    const ok = await run(async () => {
      await apiPost('/admin/capabilities', {
        name,
        description,
        sensitivity,
        components: components.map((c) => ({
          subjectType: c.subjectType,
          subjectId: c.subjectId,
          weight: Number(c.weight),
          experienceCeilingYears: c.subjectType === 'experience_area' ? Number(c.experienceCeilingYears) : undefined,
        })),
      });
      return `"${name}" was added to the capability library.`;
    });
    if (ok) onDone();
  }

  return (
    <AdminForm title="Define a new capability" onSubmit={submit} submitLabel="Create capability" busy={state.status === 'saving'}>
      <Field id="new-capability-name" label="Name" value={name} onChange={setName} />
      <Field id="new-capability-description" label="Description" value={description} onChange={setDescription} multiline />

      <div className={styles.field}>
        <label className={styles.label} htmlFor="new-capability-sensitivity">
          Sensitivity
        </label>
        <select
          id="new-capability-sensitivity"
          className={styles.input}
          value={sensitivity}
          onChange={(event) => setSensitivity(event.target.value as 'standard' | 'elevated')}
        >
          <option value="standard">Standard</option>
          <option value="elevated">Elevated</option>
        </select>
      </div>

      <h4 className={styles.formTitle}>Components</h4>
      <p className={styles.hint}>
        At least one component is required. Components cannot be added or removed after creation — a meaning change
        means deprecating this capability and defining a new one.
      </p>

      {components.map((component, index) => {
        const subjects = subjectsByType[component.subjectType];
        return (
          <div key={index} className={styles.confirm}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor={`new-capability-component-type-${index}`}>
                Component type
              </label>
              <select
                id={`new-capability-component-type-${index}`}
                className={styles.input}
                value={component.subjectType}
                onChange={(event) => {
                  const subjectType = event.target.value as CapabilityComponentSubjectType;
                  updateComponent(index, { subjectType, subjectId: subjectsByType[subjectType][0]?.id ?? '' });
                }}
              >
                {(Object.keys(COMPONENT_TYPE_LABEL) as CapabilityComponentSubjectType[]).map((type) => (
                  <option key={type} value={type}>
                    {COMPONENT_TYPE_LABEL[type]}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor={`new-capability-component-subject-${index}`}>
                {COMPONENT_TYPE_LABEL[component.subjectType]}
              </label>
              {subjects.length === 0 ? (
                <p className={styles.hint}>No active {COMPONENT_TYPE_LABEL[component.subjectType].toLowerCase()} definitions exist.</p>
              ) : (
                <select
                  id={`new-capability-component-subject-${index}`}
                  className={styles.input}
                  value={component.subjectId}
                  onChange={(event) => updateComponent(index, { subjectId: event.target.value })}
                >
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <Field
              id={`new-capability-component-weight-${index}`}
              label="Weight"
              value={component.weight}
              onChange={(value) => updateComponent(index, { weight: value })}
              hint="Relative to the other components — weights are normalized automatically."
            />

            {component.subjectType === 'experience_area' ? (
              <Field
                id={`new-capability-component-ceiling-${index}`}
                label="Experience ceiling (years)"
                value={component.experienceCeilingYears}
                onChange={(value) => updateComponent(index, { experienceCeilingYears: value })}
                hint="Years of experience treated as the maximum (a full 1.0) for this component."
              />
            ) : null}

            {components.length > 1 ? (
              <div className={styles.actions}>
                <button type="button" className={styles.secondaryButton} onClick={() => removeComponent(index)}>
                  Remove this component
                </button>
              </div>
            ) : null}
          </div>
        );
      })}

      <div className={styles.actions}>
        <button type="button" className={styles.secondaryButton} onClick={addComponent}>
          Add another component
        </button>
      </div>

      <ActionFeedback state={state} />
    </AdminForm>
  );
}

function CapabilityEditorForm({
  capability,
  onDone,
}: {
  capability: CapabilityDefinitionDetailResponse;
  onDone: () => void;
}) {
  const [name, setName] = useState(capability.name);
  const [description, setDescription] = useState(capability.description);
  const [sensitivity, setSensitivity] = useState(capability.sensitivity);
  const { state, run } = useAction();

  async function save() {
    const ok = await run(async () => {
      await apiPatch(`/admin/capabilities/${capability.id}`, { name, description, sensitivity });
      return `"${name}" was updated.`;
    });
    if (ok) onDone();
  }

  async function deprecate() {
    const ok = await run(async () => {
      await apiPost(`/admin/capabilities/${capability.id}/deprecation`, {});
      return `"${capability.name}" was retired from the library.`;
    });
    if (ok) onDone();
  }

  async function reactivate() {
    const ok = await run(async () => {
      await apiPost(`/admin/capabilities/${capability.id}/reactivation`, {});
      return `"${capability.name}" was reactivated.`;
    });
    if (ok) onDone();
  }

  return (
    <AdminForm title={`Edit ${capability.name}`} onSubmit={save} submitLabel="Save changes" busy={state.status === 'saving'}>
      <Field id={`capability-name-${capability.id}`} label="Name" value={name} onChange={setName} />
      <Field
        id={`capability-description-${capability.id}`}
        label="Description"
        value={description}
        onChange={setDescription}
        multiline
      />

      <div className={styles.field}>
        <label className={styles.label} htmlFor={`capability-sensitivity-${capability.id}`}>
          Sensitivity
        </label>
        <select
          id={`capability-sensitivity-${capability.id}`}
          className={styles.input}
          value={sensitivity}
          onChange={(event) => setSensitivity(event.target.value as 'standard' | 'elevated')}
        >
          <option value="standard">Standard</option>
          <option value="elevated">Elevated</option>
        </select>
      </div>

      <h4 className={styles.formTitle}>Components (fixed)</h4>
      <p className={styles.hint}>
        Components cannot be added, removed or reweighted here. A meaning-changing change means retiring this
        capability and defining a new one.
      </p>
      <ul className={styles.list}>
        {capability.components.map((component) => (
          <li key={component.id} className={styles.row}>
            <span className={styles.rowName}>{component.subjectName}</span>
            <span className={styles.rowMeta}>
              {COMPONENT_TYPE_LABEL[component.subjectType]} · weight {component.weight}
              {component.experienceCeilingYears !== null ? ` · ceiling ${component.experienceCeilingYears}y` : ''}
            </span>
          </li>
        ))}
      </ul>

      <ActionFeedback state={state} />

      {capability.status === 'active' ? (
        <ConfirmAction
          label="Retire this capability"
          confirmLabel="Retire it"
          busy={state.status === 'saving'}
          description={
            <>
              Retiring <strong>{capability.name}</strong> removes it from view for new role requirements and new
              employee reads. It is <strong>not</strong> deleted: every existing record and role requirement naming
              it stays exactly as it is.
            </>
          }
          onConfirm={deprecate}
        />
      ) : (
        <>
          <p className={styles.locked}>This capability is retired. Records that already reference it are unchanged.</p>
          <div className={styles.actions}>
            <button type="button" className={styles.secondaryButton} onClick={reactivate} disabled={state.status === 'saving'}>
              {state.status === 'saving' ? 'Working…' : 'Reactivate this capability'}
            </button>
          </div>
        </>
      )}
    </AdminForm>
  );
}
